/**
 * Dialog — headless primitive wrapping Radix Dialog.
 *
 * Radix's Dialog is best-in-class for focus trap, portal, scroll lock,
 * and `aria-modal` semantics (ARCHITECTURE §6). We wrap thinly:
 *   - Permission gating on <Root> (canView=false → primitive returns null)
 *   - Audit events on open / close
 *   - `data-oshon-primitive="dialog-*"` styling hooks for Phase 4
 *   - i18n-ready `messages` prop on <Close> (for the "Close" aria-label)
 *
 * "use client" is mandatory here — Radix primitives use hooks + DOM APIs
 * internally, so our wrapper inherits the client boundary. See
 * `docs/adr/primitives/002-dialog-radix-wrap.md`.
 */

'use client';

import * as RadixDialog from '@radix-ui/react-dialog';
import { forwardRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';
import { resolveMessage, type MessageBag } from '../i18n.js';

// ────────────────────────────────────────────────────────────────────────────
// Root

export interface DialogRootProps
  extends Omit<RadixDialog.DialogProps, 'onOpenChange'> {
  /**
   * Called whenever the dialog's open state changes. The primitive fires
   * `dialog:open` / `dialog:close` audit events automatically before
   * this callback runs — consumers receive the same notification either
   * way.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext` (ARCHITECTURE §10). The primitive queries
   * `can('view', resource, attrs)` on mount; denied + `mode='hidden'`
   * returns `null`. Denied + `'disabled'` or `'readOnly'` still returns
   * `null` for a dialog (there is no useful visible-but-non-interactive
   * state for a modal) and emits a `permission:denied` audit.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name passed to `permissions.can('view', resource, attrs)`.
   * Default `'dialog'`. Override for specific semantics like
   * `resource="record:delete-confirmation"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
  children: ReactNode;
}

/**
 * Dialog root. Controls open state, gates on permissions, and fires
 * audit events. Must wrap every other `Dialog.*` sub-component.
 */
function Root({
  children,
  open,
  defaultOpen,
  onOpenChange,
  permissions,
  resource = 'dialog',
  permissionAttrs,
  modal,
}: DialogRootProps) {
  const context = usePermissions();
  const effective = resolvePermissions(permissions, context);
  const emit = useAudit();

  const { granted, denyReason } = evaluatePermission(
    effective,
    'view',
    resource,
    permissionAttrs,
  );

  if (!granted) {
    // A dialog with no content can't render "readOnly" usefully, so we
    // hide under any deny mode — but still emit the denial so governance
    // can surface over-/under-permissive flows.
    emit({
      component: 'dialog',
      action: CANONICAL_ACTIONS.PERMISSION_DENIED,
      permissionResult: 'denied',
      ...(denyReason ? { denyReason } : {}),
      details: { attemptedAction: 'view', resource },
    });
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    emit({
      component: 'dialog',
      action: next ? CANONICAL_ACTIONS.DIALOG_OPEN : CANONICAL_ACTIONS.DIALOG_CLOSE,
      permissionResult: 'granted',
    });
    onOpenChange?.(next);
  };

  return (
    <RadixDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      {children}
    </RadixDialog.Root>
  );
}
Root.displayName = 'Dialog.Root';

// ────────────────────────────────────────────────────────────────────────────
// Trigger

export type DialogTriggerProps = ComponentPropsWithoutRef<typeof RadixDialog.Trigger>;

const Trigger = forwardRef<
  ElementRef<typeof RadixDialog.Trigger>,
  DialogTriggerProps
>(function DialogTrigger(props, ref) {
  return (
    <RadixDialog.Trigger
      ref={ref}
      data-oshon-primitive="dialog-trigger"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Portal

export type DialogPortalProps = RadixDialog.DialogPortalProps;

/**
 * Portal. Radix handles the portal container itself; we pass through.
 */
function Portal(props: DialogPortalProps) {
  return <RadixDialog.Portal {...props} />;
}
Portal.displayName = 'Dialog.Portal';

// ────────────────────────────────────────────────────────────────────────────
// Overlay

export type DialogOverlayProps = ComponentPropsWithoutRef<typeof RadixDialog.Overlay>;

const Overlay = forwardRef<
  ElementRef<typeof RadixDialog.Overlay>,
  DialogOverlayProps
>(function DialogOverlay(props, ref) {
  return (
    <RadixDialog.Overlay
      ref={ref}
      data-oshon-primitive="dialog-overlay"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Content

export type DialogContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content>;

/**
 * Content. Radix provides focus trap, scroll lock, ESC-to-close,
 * `aria-modal`, and `role="dialog"` — we add only the styling hook.
 */
const Content = forwardRef<
  ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(function DialogContent(props, ref) {
  return (
    <RadixDialog.Content
      ref={ref}
      data-oshon-primitive="dialog-content"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Title

export type DialogTitleProps = ComponentPropsWithoutRef<typeof RadixDialog.Title>;

const Title = forwardRef<
  ElementRef<typeof RadixDialog.Title>,
  DialogTitleProps
>(function DialogTitle(props, ref) {
  return (
    <RadixDialog.Title
      ref={ref}
      data-oshon-primitive="dialog-title"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Description

export type DialogDescriptionProps = ComponentPropsWithoutRef<typeof RadixDialog.Description>;

const Description = forwardRef<
  ElementRef<typeof RadixDialog.Description>,
  DialogDescriptionProps
>(function DialogDescription(props, ref) {
  return (
    <RadixDialog.Description
      ref={ref}
      data-oshon-primitive="dialog-description"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Close

export interface DialogCloseProps
  extends ComponentPropsWithoutRef<typeof RadixDialog.Close> {
  /**
   * i18n message bag. Reads `close` key for the default aria-label when
   * the consumer renders an icon-only close button.
   */
  messages?: MessageBag;
}

const Close = forwardRef<
  ElementRef<typeof RadixDialog.Close>,
  DialogCloseProps
>(function DialogClose({ messages, 'aria-label': ariaLabel, children, ...rest }, ref) {
  // Only fall back to the "close" message when there's no visible text
  // label (icon-only close button). If consumers render `<Close>Cancel</Close>`
  // the button's text content is its accessible name — overriding aria-label
  // would shadow it and break `getByRole('button', { name: 'Cancel' })`.
  const resolvedLabel =
    ariaLabel ??
    (children == null
      ? resolveMessage(messages, 'close', 'Close')
      : undefined);
  return (
    <RadixDialog.Close
      ref={ref}
      data-oshon-primitive="dialog-close"
      aria-label={resolvedLabel}
      {...rest}
    >
      {children}
    </RadixDialog.Close>
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Compound export

/**
 * Compound Dialog primitive. Use as:
 *
 *   <Dialog.Root>
 *     <Dialog.Trigger>Open</Dialog.Trigger>
 *     <Dialog.Portal>
 *       <Dialog.Overlay />
 *       <Dialog.Content>
 *         <Dialog.Title>Confirm</Dialog.Title>
 *         <Dialog.Description>Are you sure?</Dialog.Description>
 *         <Dialog.Close>Cancel</Dialog.Close>
 *       </Dialog.Content>
 *     </Dialog.Portal>
 *   </Dialog.Root>
 */
export const Dialog = {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
};

export type DialogProps = DialogRootProps;
